import type { CallingHours, CallingHoursWindow, VoiceAgent } from '../types';

type LeadLike = { customFields?: Record<string, unknown> | null } | undefined;

const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const WEEK_ORDER: CallingHoursWindow['day'][] = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
];

// The timezone the calling-hours gate evaluates against: the lead's own
// timezone when the agent opts in, otherwise the agent's configured fallback.
// Shared by isLeadOutsideCallingHours and resolveCallingTimezone so the rule
// lives in one place.
function selectCallingTimezone(hours: CallingHours, lead: LeadLike): string {
  return hours.useLeadTimezone
    ? resolveLeadTimezone(lead)
    : (hours.fallbackTimezone ?? 'America/Bogota');
}

// Mirrors backend-nest resolveTimezone(): use the lead's customFields.timezone
// when it is a non-empty string, otherwise fall back to America/Bogota.
export function resolveLeadTimezone(lead: LeadLike): string {
  const cf = lead?.customFields ?? undefined;
  const tz = cf?.timezone;
  if (typeof tz === 'string' && tz.length > 0) return tz;
  return 'America/Bogota';
}

// Exact client-side port of backend-nest isWithinCallingHours(). Keep in sync
// with voice-call-trigger.service.ts so the drawer pre-check matches the
// server-side gate.
export function isWithinCallingHours(hours: CallingHours, timezone: string): boolean {
  if (!hours.windows || hours.windows.length === 0) return true;

  let localDateStr: string;
  let localTimeStr: string;

  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = Object.fromEntries(
      formatter.formatToParts(new Date()).map((p) => [p.type, p.value]),
    );
    localDateStr = `${parts.year}-${parts.month}-${parts.day}`;
    localTimeStr = `${parts.hour}:${parts.minute}`;
  } catch {
    return true;
  }

  if (hours.blackoutDates?.includes(localDateStr)) return false;

  const localNow = new Date(`${localDateStr}T${localTimeStr}`);
  const dayName = DAY_NAMES[localNow.getDay()];

  return hours.windows.some(
    (w) => w.day === dayName && localTimeStr >= w.start && localTimeStr < w.end,
  );
}

// Returns true only when the agent enforces calling hours (has at least one
// window) AND the lead's local time is currently outside every window. Parity
// with the backend gate: no agent, no callingHours, or no windows means the
// call is always allowed, so this returns false in those cases.
export function isLeadOutsideCallingHours(
  agent: Pick<VoiceAgent, 'callingHours'> | undefined,
  lead: LeadLike,
): boolean {
  const hours = agent?.callingHours;
  if (!hours?.windows || hours.windows.length === 0) return false;

  return !isWithinCallingHours(hours, selectCallingTimezone(hours, lead));
}

// Returns the timezone the calling-hours gate uses for this agent/lead pair,
// or null when the agent does not enforce calling hours (no windows). Mirrors
// the selection rule in isLeadOutsideCallingHours.
export function resolveCallingTimezone(
  agent: Pick<VoiceAgent, 'callingHours'> | undefined,
  lead: LeadLike,
): string | null {
  const hours = agent?.callingHours;
  if (!hours?.windows || hours.windows.length === 0) return null;

  return selectCallingTimezone(hours, lead);
}

// Builds a human-readable schedule string for a calling-hours config, e.g.
// "Monday–Friday 09:00–18:00 · America/Bogota". Windows sharing an identical
// start/end pair are grouped; contiguous days within a pair compress into a
// range, non-contiguous days become separate entries. dayLabel resolves a day
// code to its localized label so this stays i18n-agnostic.
export function formatCallingHours(
  hours: CallingHours,
  timezone: string,
  dayLabel: (day: CallingHoursWindow['day']) => string,
): string {
  if (!hours.windows || hours.windows.length === 0) return '';

  const pairs = new Map<string, { start: string; end: string; dayIndexes: number[] }>();
  for (const window of hours.windows) {
    const dayIndex = WEEK_ORDER.indexOf(window.day);
    if (dayIndex < 0) continue;
    const key = `${window.start}-${window.end}`;
    let pair = pairs.get(key);
    if (!pair) {
      pair = { start: window.start, end: window.end, dayIndexes: [] };
      pairs.set(key, pair);
    }
    if (!pair.dayIndexes.includes(dayIndex)) pair.dayIndexes.push(dayIndex);
  }

  const entries: Array<{ earliest: number; parts: string[] }> = [];
  for (const { start, end, dayIndexes } of pairs.values()) {
    const sorted = [...dayIndexes].sort((a, b) => a - b);
    if (sorted.length === 0) continue;

    const runs: number[][] = [];
    let run: number[] = [sorted[0]];
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i] === sorted[i - 1] + 1) {
        run.push(sorted[i]);
      } else {
        runs.push(run);
        run = [sorted[i]];
      }
    }
    runs.push(run);

    const parts = runs.map((r) => {
      const first = dayLabel(WEEK_ORDER[r[0]]);
      if (r.length >= 2) {
        const last = dayLabel(WEEK_ORDER[r[r.length - 1]]);
        return `${first}–${last} ${start}–${end}`;
      }
      return `${first} ${start}–${end}`;
    });

    entries.push({ earliest: sorted[0], parts });
  }

  entries.sort((a, b) => a.earliest - b.earliest);
  const schedule = entries.flatMap((entry) => entry.parts).join(', ');
  if (schedule.length === 0) return '';

  return `${schedule} · ${timezone}`;
}
